import type { Locale } from "../i18n/siteCopy";

export type IndustryRead = {
  slug: string;
  title: string;
  summary: string;
  eyebrow: string;
  intro: string;
  sections: Array<{
    title: string;
    body: string;
  }>;
  source: string;
  date: string;
  url: string;
};

const industryReadsByLocale: Record<Locale, IndustryRead[]> = {
  en: [
    {
      slug: "mintel-kbeauty-apac-trends-2026",
      title: "Korean Beauty Trends Continue to Capture Consumers in APAC",
      summary:
        "Mintel highlights affordability, convenience, and K-culture influence as continuing drivers of K-beauty demand across APAC.",
      eyebrow: "Industry Watch",
      intro:
        "Mintel's January 5, 2026 analysis frames K-beauty as a durable APAC growth story, shaped by affordability, fast-moving innovation, and the cultural pull of Korean entertainment and lifestyle trends.",
      sections: [
        {
          title: "Demand Drivers",
          body:
            "The report points to value-for-money positioning, product convenience, and ongoing K-culture influence as the main reasons K-beauty continues to resonate with consumers across APAC markets.",
        },
        {
          title: "Category Implications",
          body:
            "Mintel emphasizes that convenience claims, healthy skin-ageing narratives, and ingredient-led communication are becoming more important in skincare and adjacent beauty categories.",
        },
        {
          title: "Why It Matters",
          body:
            "For QIANDU, the read reinforces a practical operating view: global partners are not only buying Korean beauty products, they are buying into a complete mix of value, cultural relevance, and innovation velocity.",
        },
      ],
      source: "Mintel",
      date: "January 5, 2026",
      url: "https://www.mintel.com/insights/beauty-and-personal-care/korean-beauty-trends-continue-to-capture-consumers-in-apac/",
    },
    {
      slug: "joongang-kbeauty-marketing-collaboration-2026",
      title: "From minimal to magical: Playful designs, cute collaborations reshape K-beauty marketing",
      summary:
        "Korea JoongAng Daily reports that K-beauty brands are leaning into character collaborations and social-media-first packaging to capture younger consumers.",
      eyebrow: "Industry Watch",
      intro:
        "Korea JoongAng Daily's April 16, 2026 report highlights a visible shift in K-beauty marketing, where packaging, design language, and collaboration strategy are increasingly tuned for social-first discovery and younger audiences.",
      sections: [
        {
          title: "Marketing Shift",
          body:
            "The article shows how K-beauty brands are moving beyond minimal packaging toward more playful collaborations and recognizable visual partnerships that perform well in social content environments.",
        },
        {
          title: "Consumer Relevance",
          body:
            "This direction matters because younger consumers increasingly discover products through image-led feeds, short-form video, and collectible aesthetics rather than through traditional retail messaging alone.",
        },
        {
          title: "Why It Matters",
          body:
            "For QIANDU, the read supports a broader brand-building view: product quality remains essential, but visual identity and culturally adaptable storytelling now play a stronger role in channel performance.",
        },
      ],
      source: "Korea JoongAng Daily",
      date: "April 16, 2026",
      url: "https://koreajoongangdaily.joins.com/news/2026-04-16/business/industry/From-minimal-to-magical-Playful-designs-cute-collaborations-reshape-Kbeauty-marketing/2568417",
    },
    {
      slug: "amorepacific-ces-beauty-tech-2026",
      title: "Amorepacific Showcases Innovative Technologies at CES 2026",
      summary:
        "Amorepacific presented AI skin analysis, personalized care technology, and beauty-tech devices at CES 2026, signaling continued convergence of beauty and AI.",
      eyebrow: "Beauty Tech",
      intro:
        "Amorepacific's CES 2026 presentation, published through PR Newswire on January 5, 2026, underlines how quickly beauty is converging with AI, diagnostics, and personalized device-led care.",
      sections: [
        {
          title: "Technology Focus",
          body:
            "The release spotlights AI-driven skin analysis, personalized care solutions, and beauty-tech devices, positioning advanced diagnostics and tailored routines as core innovation themes.",
        },
        {
          title: "Industry Signal",
          body:
            "CES visibility suggests that beauty technology is no longer a peripheral story. Large Korean beauty groups are presenting AI capability as part of mainstream consumer-facing brand infrastructure.",
        },
        {
          title: "Why It Matters",
          body:
            "For QIANDU, this aligns directly with the company's own positioning around AI-enabled market capability and supports a stronger bridge between K-beauty distribution and technology-led value creation.",
        },
      ],
      source: "PR Newswire / Amorepacific",
      date: "January 5, 2026",
      url: "https://www.prnewswire.com/news-releases/amorepacific-showcases-innovative-technologies-at-ces-2026-302652333.html",
    },
  ],
  zh: [
    {
      slug: "mintel-kbeauty-apac-trends-2026",
      title: "韩国美妆趋势持续影响 APAC 消费市场",
      summary:
        "Mintel 指出，价格优势、便利性与韩流文化影响仍是 K-Beauty 在亚太市场持续走强的重要驱动因素。",
      eyebrow: "行业观察",
      intro:
        "Mintel 于 2026 年 1 月 5 日发布的分析认为，K-Beauty 在 APAC 的持续增长，不只是品牌热度问题，更是价格力、产品便利性与韩流文化影响共同作用的结果。",
      sections: [
        {
          title: "需求驱动",
          body:
            "报告认为，价格优势、使用便利性与 K-Culture 的持续影响，是韩国美妆在亚太消费者中保持吸引力的关键原因。",
        },
        {
          title: "品类启示",
          body:
            "Mintel 特别强调了便利性诉求、健康肌龄表达以及成分沟通的重要性，这些都正在成为护肤与彩妆品类中的核心竞争点。",
        },
        {
          title: "对千渡的意义",
          body:
            "这份公开分析再次说明，国际市场并非只在采购韩国美妆产品本身，也在采购其背后的性价比、文化影响力与创新节奏。",
        },
      ],
      source: "Mintel",
      date: "2026年1月5日",
      url: "https://www.mintel.com/insights/beauty-and-personal-care/korean-beauty-trends-continue-to-capture-consumers-in-apac/",
    },
    {
      slug: "joongang-kbeauty-marketing-collaboration-2026",
      title: "从极简到趣味化，联名合作正在重塑韩国美妆营销",
      summary:
        "《Korea JoongAng Daily》报道，韩国美妆品牌正通过角色联名与社交媒体传播导向的包装设计争取年轻消费群体。",
      eyebrow: "行业观察",
      intro:
        "《Korea JoongAng Daily》在 2026 年 4 月 16 日的报道指出，韩国美妆品牌正在从传统极简风格转向更具传播性的联名设计与视觉表达，以适应年轻消费群体的内容消费方式。",
      sections: [
        {
          title: "营销变化",
          body:
            "报道显示，韩国美妆品牌正在通过角色联名、趣味包装与更适合社交平台传播的视觉语言，强化年轻消费者的注意力获取能力。",
        },
        {
          title: "消费逻辑",
          body:
            "对年轻消费者而言，品牌被发现的路径越来越依赖图片化内容、短视频环境与具备分享属性的产品外观，而不再仅依靠传统零售陈列。",
        },
        {
          title: "对千渡的意义",
          body:
            "这意味着韩国美妆的市场竞争，已不仅是产品质量之争，也越来越是视觉策略、内容适配能力与本地化传播效率之争。",
        },
      ],
      source: "Korea JoongAng Daily",
      date: "2026年4月16日",
      url: "https://koreajoongangdaily.joins.com/news/2026-04-16/business/industry/From-minimal-to-magical-Playful-designs-cute-collaborations-reshape-Kbeauty-marketing/2568417",
    },
    {
      slug: "amorepacific-ces-beauty-tech-2026",
      title: "爱茉莉太平洋在 CES 2026 展示 AI 美妆科技",
      summary:
        "爱茉莉太平洋在 CES 2026 展示 AI 皮肤分析、个性化护理方案与美妆科技设备，反映出美妆与 AI 融合趋势持续加深。",
      eyebrow: "美妆科技",
      intro:
        "爱茉莉太平洋于 2026 年 1 月 5 日通过 PR Newswire 发布 CES 2026 相关信息，展示了 AI 皮肤分析、个性化护理与 beauty-tech 设备的最新方向。",
      sections: [
        {
          title: "技术重点",
          body:
            "该发布重点围绕 AI 皮肤分析、个性化护理解决方案以及美妆科技设备展开，显示出诊断能力与定制化护理正在成为品牌创新核心。",
        },
        {
          title: "行业信号",
          body:
            "这说明 beauty-tech 不再只是边缘议题，韩国头部美妆集团已将 AI 能力作为面对消费者与市场展示的重要部分。",
        },
        {
          title: "对千渡的意义",
          body:
            "这与千渡当前围绕 AI 驱动市场能力的企业定位高度一致，也强化了韩国美妆分销与技术价值融合的发展方向。",
        },
      ],
      source: "PR Newswire / Amorepacific",
      date: "2026年1月5日",
      url: "https://www.prnewswire.com/news-releases/amorepacific-showcases-innovative-technologies-at-ces-2026-302652333.html",
    },
  ],
  ko: [
    {
      slug: "mintel-kbeauty-apac-trends-2026",
      title: "K-뷰티 트렌드는 APAC 소비자에게 계속 영향력을 확대하고 있습니다",
      summary:
        "Mintel은 가격 경쟁력, 편의성, K-컬처 영향력이 APAC 전역에서 K-뷰티 수요를 계속 견인하고 있다고 분석했습니다.",
      eyebrow: "산업 관찰",
      intro:
        "Mintel은 2026년 1월 5일 분석에서 K-뷰티의 APAC 성장세가 단순한 유행이 아니라 가격 경쟁력, 편의성, 그리고 K-컬처의 영향력이 결합된 결과라고 설명합니다.",
      sections: [
        {
          title: "수요 동인",
          body:
            "보고서는 합리적인 가격, 사용 편의성, 그리고 K-컬처의 지속적인 영향력을 K-뷰티 수요의 핵심 요인으로 제시합니다.",
        },
        {
          title: "카테고리 시사점",
          body:
            "Mintel은 편의성 중심 커뮤니케이션, 건강한 스킨에이징, 성분 중심 메시지가 스킨케어와 인접 카테고리에서 점점 더 중요해지고 있다고 봅니다.",
        },
        {
          title: "QIANDU 관점",
          body:
            "이는 글로벌 파트너가 한국 뷰티 제품만을 사는 것이 아니라, 그 뒤에 있는 가치, 문화적 매력, 그리고 혁신 속도까지 함께 평가하고 있음을 보여줍니다.",
        },
      ],
      source: "Mintel",
      date: "2026년 1월 5일",
      url: "https://www.mintel.com/insights/beauty-and-personal-care/korean-beauty-trends-continue-to-capture-consumers-in-apac/",
    },
    {
      slug: "joongang-kbeauty-marketing-collaboration-2026",
      title: "미니멀에서 매지컬로, 협업이 K-뷰티 마케팅을 재편하고 있습니다",
      summary:
        "Korea JoongAng Daily는 K-뷰티 브랜드들이 캐릭터 협업과 소셜미디어 중심 패키징으로 젊은 소비자를 공략하고 있다고 전했습니다.",
      eyebrow: "산업 관찰",
      intro:
        "Korea JoongAng Daily의 2026년 4월 16일 보도는 K-뷰티 마케팅이 전통적인 미니멀 감성에서 더 확산력 있는 협업형 디자인과 시각 전략으로 이동하고 있음을 보여줍니다.",
      sections: [
        {
          title: "마케팅 변화",
          body:
            "기사에 따르면 K-뷰티 브랜드들은 캐릭터 협업, 시각적 포장, 소셜 콘텐츠 친화적 표현을 통해 젊은 소비자의 관심을 더 적극적으로 확보하고 있습니다.",
        },
        {
          title: "소비자 맥락",
          body:
            "젊은 소비자는 전통 리테일보다 이미지 중심 피드, 숏폼 영상, 공유 가능한 디자인을 통해 브랜드를 발견하는 경우가 많아지고 있습니다.",
        },
        {
          title: "QIANDU 관점",
          body:
            "이는 제품력뿐 아니라 비주얼 전략, 콘텐츠 적합성, 현지 커뮤니케이션 실행력이 한국 뷰티 브랜드 성과에 더 직접적으로 연결되고 있음을 시사합니다.",
        },
      ],
      source: "Korea JoongAng Daily",
      date: "2026년 4월 16일",
      url: "https://koreajoongangdaily.joins.com/news/2026-04-16/business/industry/From-minimal-to-magical-Playful-designs-cute-collaborations-reshape-Kbeauty-marketing/2568417",
    },
    {
      slug: "amorepacific-ces-beauty-tech-2026",
      title: "아모레퍼시픽, CES 2026에서 AI 뷰티 기술 공개",
      summary:
        "아모레퍼시픽은 CES 2026에서 AI 피부 분석과 개인화된 케어 기술, 뷰티 디바이스를 선보이며 뷰티와 AI 융합 흐름을 강조했습니다.",
      eyebrow: "뷰티 테크",
      intro:
        "아모레퍼시픽은 2026년 1월 5일 PR Newswire를 통해 CES 2026 공개 내용을 발표하며 AI 피부 분석, 맞춤형 케어, 뷰티 디바이스 방향성을 제시했습니다.",
      sections: [
        {
          title: "기술 초점",
          body:
            "발표는 AI 기반 피부 분석, 개인화된 관리 솔루션, 뷰티 테크 디바이스를 중심으로 구성되며 정밀 진단과 맞춤형 케어가 핵심 혁신 축임을 보여줍니다.",
        },
        {
          title: "산업 신호",
          body:
            "이는 뷰티 테크가 더 이상 주변적인 주제가 아니라, 한국 대형 뷰티 그룹의 공식 시장 커뮤니케이션 중심으로 들어왔음을 의미합니다.",
        },
        {
          title: "QIANDU 관점",
          body:
            "QIANDU의 AI 기반 시장 역량 포지셔닝과도 직접 연결되며, K-뷰티 유통과 기술 가치가 함께 결합되는 방향을 뒷받침합니다.",
        },
      ],
      source: "PR Newswire / Amorepacific",
      date: "2026년 1월 5일",
      url: "https://www.prnewswire.com/news-releases/amorepacific-showcases-innovative-technologies-at-ces-2026-302652333.html",
    },
  ],
  vi: [
    {
      slug: "mintel-kbeauty-apac-trends-2026",
      title: "Xu hướng K-Beauty tiếp tục mở rộng ảnh hưởng tại APAC",
      summary:
        "Mintel cho rằng giá hợp lý, tính tiện dụng và ảnh hưởng của văn hóa Hàn Quốc vẫn là các động lực chính thúc đẩy nhu cầu K-Beauty tại APAC.",
      eyebrow: "Góc nhìn ngành",
      intro:
        "Phân tích ngày 5 tháng 1 năm 2026 của Mintel cho thấy đà tăng của K-Beauty tại APAC không chỉ đến từ độ nổi tiếng thương hiệu mà còn từ lợi thế giá, tính tiện dụng và sức lan tỏa của văn hóa Hàn Quốc.",
      sections: [
        {
          title: "Động lực nhu cầu",
          body:
            "Báo cáo nhấn mạnh giá hợp lý, sự tiện dụng và ảnh hưởng của K-culture là các yếu tố chính giữ cho K-Beauty tiếp tục hấp dẫn người tiêu dùng trong khu vực.",
        },
        {
          title: "Hàm ý danh mục",
          body:
            "Mintel chỉ ra rằng thông điệp về tính tiện dụng, healthy skin-ageing và giao tiếp dựa trên thành phần đang ngày càng quan trọng trong skincare và các nhóm làm đẹp liên quan.",
        },
        {
          title: "Ý nghĩa với QIANDU",
          body:
            "Điều này củng cố một thực tế vận hành rõ ràng: đối tác quốc tế không chỉ tìm mua sản phẩm Hàn Quốc, mà còn tìm kiếm giá trị, sức ảnh hưởng văn hóa và tốc độ đổi mới của hệ sinh thái K-Beauty.",
        },
      ],
      source: "Mintel",
      date: "05/01/2026",
      url: "https://www.mintel.com/insights/beauty-and-personal-care/korean-beauty-trends-continue-to-capture-consumers-in-apac/",
    },
    {
      slug: "joongang-kbeauty-marketing-collaboration-2026",
      title: "Từ tối giản đến giàu tính giải trí, các màn hợp tác đang tái định hình marketing K-Beauty",
      summary:
        "Korea JoongAng Daily cho biết các thương hiệu K-Beauty đang đẩy mạnh hợp tác nhân vật và thiết kế bao bì theo định hướng social media để thu hút người tiêu dùng trẻ.",
      eyebrow: "Góc nhìn ngành",
      intro:
        "Bài viết ngày 16 tháng 4 năm 2026 của Korea JoongAng Daily cho thấy marketing K-Beauty đang chuyển dịch từ tối giản truyền thống sang các chiến lược hình ảnh dễ lan truyền hơn, phù hợp với hành vi tiêu dùng nội dung của khách hàng trẻ.",
      sections: [
        {
          title: "Dịch chuyển marketing",
          body:
            "Bài báo mô tả cách các thương hiệu K-Beauty đang dùng hợp tác nhân vật, bao bì giàu tính thị giác và thiết kế thân thiện với social content để thu hút sự chú ý.",
        },
        {
          title: "Bối cảnh tiêu dùng",
          body:
            "Người tiêu dùng trẻ ngày càng khám phá thương hiệu qua hình ảnh, short-form video và các thiết kế có khả năng chia sẻ, thay vì chỉ qua trải nghiệm kệ hàng truyền thống.",
        },
        {
          title: "Ý nghĩa với QIANDU",
          body:
            "Điều này cho thấy sức cạnh tranh của thương hiệu mỹ phẩm Hàn Quốc ngày càng phụ thuộc không chỉ vào sản phẩm mà còn vào năng lực thị giác, nội dung và truyền thông bản địa hóa.",
        },
      ],
      source: "Korea JoongAng Daily",
      date: "16/04/2026",
      url: "https://koreajoongangdaily.joins.com/news/2026-04-16/business/industry/From-minimal-to-magical-Playful-designs-cute-collaborations-reshape-Kbeauty-marketing/2568417",
    },
    {
      slug: "amorepacific-ces-beauty-tech-2026",
      title: "Amorepacific giới thiệu công nghệ làm đẹp AI tại CES 2026",
      summary:
        "Amorepacific trình bày công nghệ phân tích da bằng AI, giải pháp chăm sóc cá nhân hóa và thiết bị beauty-tech tại CES 2026, cho thấy xu hướng hội tụ giữa beauty và AI.",
      eyebrow: "Beauty Tech",
      intro:
        "Thông tin do PR Newswire đăng ngày 5 tháng 1 năm 2026 cho thấy Amorepacific đã dùng CES 2026 để nhấn mạnh vai trò ngày càng lớn của AI, chẩn đoán da và chăm sóc cá nhân hóa trong ngành beauty.",
      sections: [
        {
          title: "Trọng tâm công nghệ",
          body:
            "Bản công bố tập trung vào AI skin analysis, giải pháp chăm sóc cá nhân hóa và thiết bị beauty-tech, cho thấy định hướng đổi mới đang xoay quanh chẩn đoán chính xác và routine cá nhân hóa.",
        },
        {
          title: "Tín hiệu ngành",
          body:
            "Việc xuất hiện tại CES cho thấy beauty-tech không còn là một chủ đề bên lề mà đã trở thành một phần của câu chuyện thương hiệu quy mô lớn trong ngành làm đẹp Hàn Quốc.",
        },
        {
          title: "Ý nghĩa với QIANDU",
          body:
            "Điều này phù hợp trực tiếp với định vị AI-driven của QIANDU và củng cố hướng phát triển gắn kết giữa phân phối K-Beauty và giá trị công nghệ.",
        },
      ],
      source: "PR Newswire / Amorepacific",
      date: "05/01/2026",
      url: "https://www.prnewswire.com/news-releases/amorepacific-showcases-innovative-technologies-at-ces-2026-302652333.html",
    },
  ],
};

export function getIndustryReads(locale: Locale) {
  return industryReadsByLocale[locale];
}

export function getIndustryRead(locale: Locale, slug: string) {
  return industryReadsByLocale[locale].find((item) => item.slug === slug) ?? null;
}
